import { useEffect, useState } from 'react'
import { ExternalLink, Loader2, Route, X } from 'lucide-react'
import { apiClient } from '@/lib/api'
import { toast } from '@/hooks/use-toast'
import type { AircraftDetail, LiveAircraft, WatchRuleInput } from '@/types/airTraffic'
import type { SelectionOverlay } from '../AirMap'
import { AIR, CLASS_COLOR, EMERGENCY_LABEL, FONT_MONO, classify, fmtAgo, fmtAlt } from '../theme'
import { ActionButton, Chip, Empty, KV, SectionLabel, downloadReport, errorText } from '../ui'

const SEV_COLOR: Record<string, string> = { CRITICAL: AIR.emergency, HIGH: AIR.military, MEDIUM: AIR.watched, LOW: AIR.muted }

interface Props {
  hex: string
  live?: LiveAircraft
  watched: Set<string>
  onClose: () => void
  onSelection: (s: SelectionOverlay) => void
  onWatch: (rule: WatchRuleInput) => Promise<void>
  onOpenOperator: (name: string) => void
  onOpenAirport: (icao: string, lon?: number, lat?: number) => void
}

export default function AircraftInspector({ hex, live, watched, onClose, onSelection, onWatch, onOpenOperator, onOpenAirport }: Props) {
  const [detail, setDetail] = useState<AircraftDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [path, setPath] = useState<[number, number][] | null>(null)
  const [pathBusy, setPathBusy] = useState(false)
  const [showHistory, setShowHistory] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setDetail(null)
    setPath(null)
    setShowHistory(false)
    apiClient
      .airAircraft(hex, true)
      .then((d) => !cancelled && setDetail(d))
      .catch((e) => !cancelled && setError(errorText(e)))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [hex])

  const route = detail?.fr24.route
  useEffect(() => {
    const pt = (a?: { lon?: number; lat?: number } | null): [number, number] | null =>
      a && a.lon != null && a.lat != null ? [a.lon, a.lat] : null
    onSelection({
      hex,
      fr24Path: path || undefined,
      history: showHistory ? detail?.history.path : undefined,
      routes: route
        ? { [hex]: { origin: pt(route.origin), destination: pt(route.destination), ended: !!detail?.fr24.summary?.flight_ended } }
        : undefined,
    })
  }, [hex, path, showHistory, detail, route, onSelection])

  const fr = detail?.fr24
  const s = fr?.summary
  // OpenSky-only aircraft carry no type/registration; fill gaps from the FR24 summary.
  const base: LiveAircraft | undefined = live || detail?.aircraft
  const a: LiveAircraft | undefined = base && {
    ...base,
    type: base.type || s?.type,
    reg: base.reg || s?.reg,
    op: base.op || s?.operating_as || s?.painted_as,
  }
  const cls = a ? classify(a, watched) : 'civil'

  const loadPath = async () => {
    setPathBusy(true)
    try {
      const t = await apiClient.airAircraftTrack(hex)
      setPath(t.points.map((p) => [p[0], p[1]]))
      toast({ title: t.cached ? 'Flight path loaded (cached, no credits)' : `Flight path loaded (${t.credits} FR24 credits)` })
    } catch (e) {
      toast({ title: 'Flight path unavailable', description: errorText(e), variant: 'destructive' })
    } finally {
      setPathBusy(false)
    }
  }

  const isWatched = watched.has(hex) || detail?.watched
  const csPrefix = a?.cs?.match(/^[A-Z]{3,4}/)?.[0]

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-start justify-between gap-2 border-b px-3 py-2.5" style={{ borderColor: AIR.line }}>
        <div className="min-w-0">
          <div className="truncate text-[18px] font-semibold leading-tight" style={{ color: CLASS_COLOR[cls], fontFamily: FONT_MONO }}>
            {a?.cs || a?.reg || hex.toUpperCase()}
          </div>
          <div className="truncate text-[12px]" style={{ color: AIR.muted }}>
            {[a?.desc || a?.type, a?.op].filter(Boolean).join(' · ') || 'Unknown type'}
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1">
            {a?.emg && <Chip color={AIR.emergency} solid>{EMERGENCY_LABEL[a.emg] || a.emg}</Chip>}
            {a?.mil && <Chip color={AIR.military}>Military</Chip>}
            {a?.ladd && <Chip color={AIR.privacy}>LADD</Chip>}
            {a?.pia && <Chip color={AIR.privacy}>PIA</Chip>}
            {isWatched && <Chip color={AIR.watched}>Watched</Chip>}
            {detail && !detail.live && <Chip color={AIR.muted}>Not received now</Chip>}
          </div>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="rounded-sm p-1 hover:bg-white/5">
          <X className="h-4 w-4" style={{ color: AIR.muted }} />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pb-4">
        {loading && !a && (
          <div className="flex items-center gap-2 px-3 py-4 text-[12px]" style={{ color: AIR.muted }}>
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Looking up aircraft…
          </div>
        )}
        {error && <Empty>{error}</Empty>}

        {detail?.photo?.thumbnail_url && (
          <a href={detail.photo.credit_link} target="_blank" rel="noopener noreferrer" className="block">
            <img src={detail.photo.thumbnail_url} alt={`${a?.reg || hex} aircraft`} className="aspect-[16/9] w-full object-cover" loading="lazy" />
            <div className="px-3 pt-1 text-[10px]" style={{ color: AIR.dim }}>
              © {detail.photo.photographer} · planespotters.net
            </div>
          </a>
        )}

        {a && (
          <>
            <SectionLabel>Identity</SectionLabel>
            <KV k="ICAO24" v={hex.toUpperCase()} />
            <KV k="Registration" v={a.reg} />
            <KV k="Type" v={a.type} />
            <KV
              k="Operator"
              v={
                a.op ? (
                  <button type="button" className="underline-offset-2 hover:underline" onClick={() => onOpenOperator(a.op!)}>
                    {a.op}
                  </button>
                ) : undefined
              }
            />
            <KV k="Country" v={a.cty} />

            <SectionLabel right={<span className="text-[10px]" style={{ color: AIR.dim }}>{a.pts ? fmtAgo(a.pts) : ''}</span>}>
              Telemetry
            </SectionLabel>
            <KV k="Altitude" v={fmtAlt(a)} />
            <KV k="Ground speed" v={a.gs != null ? `${a.gs} kt` : undefined} />
            <KV k="Track" v={a.trk != null ? `${Math.round(a.trk)}°` : undefined} />
            <KV k="Vertical rate" v={a.vr != null ? `${a.vr > 0 ? '+' : ''}${a.vr} ft/min` : undefined} />
            <KV k="Squawk" v={a.sq} />
            <KV k="Position" v={`${a.lat.toFixed(4)}, ${a.lon.toFixed(4)}`} />
            <KV k="Sources" v={a.src?.map((x) => (x === 'adsb' ? 'adsb.lol' : 'OpenSky')).join(', ')} mono={false} />
          </>
        )}

        {detail && (
          <>
            <SectionLabel right={fr?.status === 'live' ? <span className="text-[10px]" style={{ color: AIR.dim }}>1 FR24 credit</span> : null}>
              Flight
            </SectionLabel>
            {s ? (
              <>
                <RouteLine route={route} onOpenAirport={onOpenAirport} />
                <KV k="Flight" v={s.flight} />
                <KV k="Operated as" v={s.operating_as} />
                <KV k="Departed" v={s.datetime_takeoff?.replace('T', ' ').slice(0, 16)} />
                <KV k="Status" v={s.flight_ended ? `Landed ${s.datetime_landed?.slice(11, 16) || ''}` : 'Airborne'} mono={false} />
                <div className="px-3 pt-2">
                  <ActionButton onClick={loadPath} busy={pathBusy} disabled={!!path} title="Fetch the full flown track from Flightradar24">
                    <Route className="h-3.5 w-3.5" />
                    {path ? 'Full flight path shown' : 'Full flight path · 40 credits'}
                  </ActionButton>
                </div>
              </>
            ) : (
              <Empty>
                {fr?.status === 'not_found'
                  ? 'Flightradar24 has no flight for this aircraft in the last 14 hours. Military and privacy-listed aircraft usually aren’t published there.'
                  : fr?.message || 'No flight details.'}
              </Empty>
            )}

            <SectionLabel>Intel</SectionLabel>
            {detail.correlation ? (
              <div className="mx-3 mb-1 rounded-sm px-2.5 py-2" style={{ background: AIR.panel2, boxShadow: `inset 0 0 0 1px ${SEV_COLOR[detail.correlation.severity]}55` }}>
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider" style={{ color: SEV_COLOR[detail.correlation.severity] }}>
                  {detail.correlation.severity} correlation
                </div>
                <ul className="space-y-0.5 text-[12px]" style={{ color: AIR.text }}>
                  {detail.correlation.reasons.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                  {detail.correlation.nearby_aois.map((x, i) => (
                    <li key={`aoi${i}`}>Near AOI {x.name}{x.distance_km != null ? ` (${x.distance_km} km)` : ''}</li>
                  ))}
                  {detail.correlation.nearby_vessels.map((x, i) => (
                    <li key={`v${i}`}>Near vessel {x.name}{x.distance_km != null ? ` (${x.distance_km} km)` : ''}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <Empty>No correlation with your AOIs, vessels or watched entities right now.</Empty>
            )}
            {detail.entity && (
              <>
                <KV k="Entity record" v={detail.entity.name} />
                <KV k="Observations" v={detail.entity.observations} />
                <KV k="First seen" v={detail.entity.first_seen?.slice(0, 10)} />
                <KV k="Seen by" v={detail.entity.sources?.join(', ')} mono={false} />
              </>
            )}
            <KV k="30-day sightings" v={detail.history.sightings ? `${detail.history.sightings} on ${detail.history.days_active} days` : 'None recorded'} mono={false} />
            <KV k="Callsigns used" v={detail.history.callsigns?.join(', ')} />
            {detail.history.path && detail.history.path.length > 1 && (
              <div className="px-3 pt-1">
                <label className="flex cursor-pointer items-center gap-2 text-[12px]" style={{ color: AIR.text }}>
                  <input id="air-show-history" type="checkbox" checked={showHistory} onChange={(e) => setShowHistory(e.target.checked)} className="h-3.5 w-3.5 accent-violet-400" />
                  Show 30-day sightings on map
                </label>
              </div>
            )}

            {detail.alerts.length > 0 && (
              <>
                <SectionLabel>Alerts</SectionLabel>
                {detail.alerts.slice(0, 5).map((al) => (
                  <div key={al.id} className="px-3 py-1 text-[12px]" style={{ color: AIR.text }}>
                    <span style={{ color: AIR.muted, fontFamily: FONT_MONO }}>{al.ts.slice(5, 16).replace('T', ' ')} </span>
                    {al.title}
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 border-t px-3 py-2.5" style={{ borderColor: AIR.line }}>
        <ActionButton
          tone="primary"
          onClick={() => downloadReport('aircraft', { hex }, a?.reg || a?.cs || hex)}
          title="Aircraft profile PDF: identity, route, 30-day history, correlations"
        >
          Report
        </ActionButton>
        <ActionButton
          disabled={!!isWatched}
          onClick={() => onWatch({ kind: 'hex', value: hex, label: `${a?.cs || a?.reg || hex.toUpperCase()}${a?.type ? ` (${a.type})` : ''}` })}
        >
          {isWatched ? 'Watching' : 'Watch aircraft'}
        </ActionButton>
        {csPrefix && (
          <ActionButton onClick={() => onWatch({ kind: 'callsign_prefix', value: csPrefix })} title={`Alert on any callsign starting ${csPrefix}`}>
            Watch {csPrefix}*
          </ActionButton>
        )}
        <a
          href={`https://globe.adsb.lol/?icao=${hex}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-1.5 py-1.5 text-[11px]"
          style={{ color: AIR.muted }}
        >
          adsb.lol <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </div>
  )
}

function RouteLine({ route, onOpenAirport }: { route: AircraftDetail['fr24']['route']; onOpenAirport: Props['onOpenAirport'] }) {
  if (!route) return null
  const end = (p: typeof route.origin, label: string) =>
    p?.icao ? (
      <button
        type="button"
        onClick={() => onOpenAirport(p.icao!, p.lon, p.lat)}
        className="min-w-0 text-left hover:opacity-80"
        title={p.name}
      >
        <div className="text-[16px] font-semibold" style={{ color: AIR.bright, fontFamily: FONT_MONO }}>
          {p.iata || p.icao}
        </div>
        <div className="truncate text-[11px]" style={{ color: AIR.muted }}>
          {p.city || p.name}
        </div>
      </button>
    ) : (
      <div className="text-[12px]" style={{ color: AIR.muted }}>
        {label} unknown
      </div>
    )
  return (
    <div className="mx-3 mb-1.5 grid grid-cols-[1fr_auto_1fr] items-center gap-2 rounded-sm px-2.5 py-2" style={{ background: AIR.panel2 }}>
      {end(route.origin, 'Origin')}
      <span style={{ color: AIR.watched }}>→</span>
      <div className="text-right">{end(route.destination, 'Destination')}</div>
    </div>
  )
}
